import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt';
import { FiChevronLeft, FiFileText, FiMoreHorizontal, FiTrash2 } from 'react-icons/fi';
import { confirm } from '@/lib/confirm';
import { useSession } from '@/hooks/useSession';
import { ProjectsContext } from '../provider';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import toast from 'react-hot-toast';
import Card from '@/components/custom/card/card';
import { EmptyState } from '@/components/custom/empty-state';
import { Dropdown } from '@/components/custom/dropdown/dropdown';
import { CardAvatarStack } from '@/components/ui/card-avatar-stack';
import { PlayfulCard, PlayfulCardButton } from '@/components/custom/playful-card';
import { TypewriterText } from '@/components/custom/typewriter-text';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { ProjectSchedulePlayful } from '../Playful/ProjectSchedule';
import { ProjectsCardCommentPlayful } from '../Playful/ProjectsCardComment';
import usePermission from '@/hooks/usePermission';
import { Loader } from '@/components/custom/loader';
import { useClient } from 'jsonapi-react';
import { useTranslation } from 'react-i18next';
import { DashboardContent } from '@/layouts/dashboard';
import { Button, ButtonBack } from '@/components/ui/button';
import { IconWrapper } from '@/components/custom/icon';
import { Badge } from '@/components/ui/badge';
import { PostActionsButton, PostCardWrapper } from '@/components/custom/post';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ProjectCard,
  ProjectCardFile,
  ProjectCardHeader,
  ProjectCardFileTitle,
  ProjectWorkspaceHeader,
  ProjectWorkspaceHeaderContent,
  ProjectWorkspaceTitle,
  ProjectWorkspaceDateTime,
  ProjectWorkspaceDateTimeWrapper,
  ProjectTimeline,
  ProjectTimelineItem,
  ProjectTimelineVersion,
  ProjectTimelineGroup,
  ProjectTimelineText,
  ProjectTimelineCreate
} from '@/components/custom/project';

const PLAYFUL_TAB_CLASSNAME = 'data-[state=active]:bg-[var(--color-mission)] data-[state=active]:text-white data-[state=active]:font-extrabold data-[state=active]:shadow-[0_3px_0_var(--color-mission-dark)] data-[state=active]:-translate-y-px';

const welcomeMessage = 'Acompanhe as versões enviadas, comente e aprove quando o projeto estiver pronto! 📋';

const DEFAULT_BACK_TO = '/projetos/grupos';

// Conteúdo do workspace do projeto no tema playful do professor: mesmo layout
// de ../Playful/ProjectsContent.jsx (aluno) — aside fixo com mascote + balão à
// esquerda, botão de voltar ao lado do título, mesmas tabs Versões/Sobre — e
// mesma lógica de dados/permissões dali e de ../projects-content.jsx
// (corporativa): aprovar projeto, avaliar/excluir versão, comentar e
// gerenciar participantes já funcionam via usePermission/authorityLevel. O
// botão de voltar usa `backTo` recebido por navegação (ver
// PersonalizedProjectRowTeacher) pra levar de volta ao grupo ou à listagem de
// onde o professor veio; sem state (URL direta/refresh), cai em `/projetos/grupos`.
// Sem upload de nova versão (nunca aparece pra professor) nem link "Sobre o
// projeto" separado (virou aba).
export function ProjectsContentPlayfulTeacher({ projectId }) {
  const { t } = useTranslation();
  const client = useClient();
  const location = useLocation();
  const [versionCommentDraft, setVersionCommentDraft] = useState('');
  const [showModal, setShowModal] = useState({
    addParticipant: false,
    removeParticipant: false
  });
  const { session } = useSession();
  const [allProfiles, setAllProfiles] = useState();
  const [versionUser, setVersionUser] = useState({});
  const [versionUserLoading, setVersionUserLoading] = useState(true);
  const [comments, setComments] = useState([]);

  const {
    selectedProject,
    selectedProjectVersion,
    getProject,
    getProjectVersion,
    confirmApproveProject,
    getAllStudents,
    studentFilter,
    filteredStudents,
    addStudentToSelectedProject,
    deleteStudentFromSelectedProject,
    deleteProjectVersion,
    loading,
    createProjectVersionComment,
    deleteProjectVersionComment
  } = useContext(ProjectsContext);

  const authorityLevel = useAuthorityLevel(session);

  useEffect(() => {
    if (projectId) {
      getProject(true, projectId);
    }
  }, [projectId]);

  const getAllProfiles = async () => {
    const { data, error } = await client.fetch('profiles');
    if (error) {
      toast.error(t('toast.errorGetParticipants'));
    } else {
      setAllProfiles(
        data
          .map(item => item.name)
          .reduce((item, i) => {
            return `${item},${i}`;
          })
      );
    }
  };

  const destroyStudentForm = async student => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteStudentFromProject'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.delete'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteStudentFromSelectedProject(student.id);
      toast.success(t('toast.successRemoveStudentFromProject'));
    }
  };

  const getUser = async () => {
    setVersionUserLoading(true);
    const { data, error } = await client.fetch(`users/${selectedProjectVersion['user_id']}`);
    if (error) {
      toast.error('Erro ao buscar informações de quem enviou esta versão.');
    } else {
      setVersionUser(data);
      setVersionUserLoading(false);
    }
  };

  const fetchComments = async () => {
    const { data, error } = await client.fetch(`/comments?filter[project_version_id]=${selectedProjectVersion?.id}`);

    if (!error) {
      setComments(data);
    } else {
      toast.error('Erro ao buscar comentários');
    }
  };

  const handleDeleteComment = async commentId => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteCommentTitle'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteProjectVersionComment(commentId);
      setComments(prev => prev.filter(item => item.id !== commentId));
    }
  };

  useEffect(() => {
    if (selectedProjectVersion && selectedProjectVersion.user_id !== null) {
      getUser();
    }

    fetchComments();
  }, [selectedProjectVersion]);

  useEffect(() => {
    getAllProfiles();
  }, []);

  const commentsFiltered = comments?.filter(comment => comment?.['comment-parent-id'] === null);

  const backTo = location.state?.backTo || DEFAULT_BACK_TO;

  return (
    <DashboardContent
      variant="plain"
      className="!pt-14"
    >
      {loading && !selectedProject && <Loader />}

      {selectedProject && (
        <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
          <aside className="flex flex-col items-center self-start rounded-[24px] bg-bright p-6 shadow-card lg:sticky">
            <img
              src={JOINHA}
              alt="Personagem do professor"
              className="pointer-events-none h-[220px] w-auto object-contain"
            />

            <div className="mt-4 w-full rounded-[16px] rounded-tl-none bg-white p-4">
              <p
                className="mb-0 text-sm font-bold leading-relaxed"
                style={{ color: '#222731' }}
              >
                <TypewriterText text={welcomeMessage} />
              </p>
            </div>
          </aside>

          <div>
            <ProjectWorkspaceHeader className="flex-wrap items-start">
              <Link
                to={backTo}
                className="mt-1"
              >
                <ButtonBack>
                  <span className="inline-flex size-8 items-center justify-center rounded-sm bg-mission-light p-1.5 text-mission">
                    <FiChevronLeft />
                  </span>
                </ButtonBack>
              </Link>

              <ProjectWorkspaceHeaderContent>
                <ProjectWorkspaceDateTimeWrapper className="mb-2">
                  <ProjectWorkspaceTitle>{selectedProject.name}</ProjectWorkspaceTitle>
                  {selectedProject.approved ? (
                    <Badge
                      variant="success-soft"
                      size="sm"
                    >
                      {t('exams.statusInApproved')}
                    </Badge>
                  ) : (
                    <Badge
                      variant="amber-soft"
                      size="sm"
                    >
                      {t('exams.inProgress')}
                    </Badge>
                  )}
                </ProjectWorkspaceDateTimeWrapper>
                <ProjectWorkspaceDateTimeWrapper>
                  <ProjectWorkspaceDateTime>
                    {t('projects.createdIn')} {moment(selectedProject['created-at']).format(t('datesForm.formatDate'))}
                  </ProjectWorkspaceDateTime>
                </ProjectWorkspaceDateTimeWrapper>
              </ProjectWorkspaceHeaderContent>
            </ProjectWorkspaceHeader>

            <Tabs defaultValue="versoes">
              <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
                <TabsList className="w-auto border border-neutral-lighter bg-white">
                  <TabsTrigger
                    value="versoes"
                    className={PLAYFUL_TAB_CLASSNAME}
                  >
                    Versões
                  </TabsTrigger>
                  <TabsTrigger
                    value="sobre"
                    className={PLAYFUL_TAB_CLASSNAME}
                  >
                    Sobre o projeto
                  </TabsTrigger>
                </TabsList>

                {usePermission('Aprovar projetos', 'Projetos') && selectedProjectVersion && !selectedProject.approved ? (
                  <PlayfulCardButton
                    className="w-auto px-6"
                    onClick={confirmApproveProject}
                  >
                    Aprovar projeto
                  </PlayfulCardButton>
                ) : null}
              </div>

              <TabsContent
                value="versoes"
                className="flex flex-col gap-4 lg:flex-row lg:items-start"
              >
                <div className="flex-1">
                  {loading && <Loader />}

                  {selectedProjectVersion && !loading && (
                    <PostCardWrapper>
                      <ProjectCard className="bg-bright">
                        {!versionUserLoading && (
                          <ProjectCardHeader>
                            <CardAvatarStack
                              data={[versionUser]}
                              disableUserLink
                            />
                            {versionUser.name}
                          </ProjectCardHeader>
                        )}
                        <ProjectCardFile>
                          <IconWrapper>
                            <FiFileText />
                          </IconWrapper>
                          <ProjectCardFileTitle>{selectedProjectVersion?.file?.filename?.toUpperCase()}</ProjectCardFileTitle>
                        </ProjectCardFile>
                        {selectedProjectVersion.file?.url && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                              className="border-mission-light text-mission hover:border-mission hover:bg-mission hover:text-white"
                            >
                              <a
                                href={selectedProjectVersion.file?.url}
                                target="_blank"
                                rel="noreferrer noopener"
                              >
                                Avaliar
                              </a>
                            </Button>

                            {(authorityLevel === 'admin' || selectedProjectVersion.user_id === session.user.id) && !selectedProject.approved && (
                              <PostActionsButton
                                isDanger
                                className="absolute right-4 top-4"
                              >
                                <FiTrash2
                                  size={16}
                                  onClick={async () => {
                                    const confirmed = await confirm({
                                      title: t('projects.warningDeleteProjectVersionTitle'),
                                      description: t('projects.warningDeleteCannotBeUndoneText'),
                                      confirmText: t('button.yes'),
                                      cancelText: t('button.no')
                                    });
                                    if (confirmed) {
                                      deleteProjectVersion(selectedProjectVersion);
                                    }
                                  }}
                                />
                              </PostActionsButton>
                            )}
                          </>
                        )}
                      </ProjectCard>

                      {commentsFiltered?.length > 0 && (
                        <div className="flex w-full flex-col gap-3">
                          {commentsFiltered.map(c => (
                            <ProjectsCardCommentPlayful
                              key={c.id}
                              comment={c}
                              comments={comments}
                              onDelete={handleDeleteComment}
                            />
                          ))}
                        </div>
                      )}

                      {!selectedProject.approved && usePermission('Postar e comentar em forum', 'Projetos') ? (
                        <div className="w-full">
                          <Input
                            placeholder={t('textsCommon.placeholderSendYourComment')}
                            aria-label={t('textsCommon.placeholderSendYourComment')}
                            value={versionCommentDraft}
                            onChange={e => setVersionCommentDraft(e.target.value)}
                            onKeyPress={e => {
                              if (e.key === 'Enter') {
                                createProjectVersionComment(versionCommentDraft);
                                setVersionCommentDraft('');
                              }
                            }}
                            className="bg-bright"
                          />
                          <p className="mb-0 text-right text-xs text-neutral">{t('textsCommon.textPressEnter')}</p>
                        </div>
                      ) : null}
                    </PostCardWrapper>
                  )}

                  {!selectedProjectVersion && !loading && (
                    <EmptyState
                      type="data"
                      title={t('emptyState.missingDataTitle')}
                      text={authorityLevel === 'admin' ? t('emptyState.textNoDataYet') : t('emptyState.submittingNewVersionText')}
                    />
                  )}
                </div>

                {selectedProject['project-versions']?.length > 0 && (
                  <PlayfulCard className="p-5 lg:w-[350px] lg:flex-none">
                    <h4
                      className="mb-3 text-sm font-extrabold"
                      style={{ color: '#222731' }}
                    >
                      Versões enviadas
                    </h4>
                    <ProjectTimeline>
                      {selectedProject['project-versions'].map(v => (
                        <ProjectTimelineItem
                          key={v.id}
                          onClick={() => getProjectVersion(v.id)}
                          active={selectedProjectVersion && v.id === selectedProjectVersion.id}
                        >
                          <ProjectTimelineVersion className="bg-mission text-white">V.{v.version}</ProjectTimelineVersion>
                          <ProjectTimelineGroup>
                            <ProjectTimelineText>
                              {t('projects.versionProject')} {v.version}.0
                            </ProjectTimelineText>
                            <ProjectTimelineCreate>
                              {t('projects.createdAt')} {moment(v.created_at).format(t('datesForm.formatDate'))}
                            </ProjectTimelineCreate>
                          </ProjectTimelineGroup>
                        </ProjectTimelineItem>
                      ))}
                      {selectedProject.approved && <ProjectTimelineItem approved>🎉 Projeto aprovado!</ProjectTimelineItem>}
                    </ProjectTimeline>
                  </PlayfulCard>
                )}
              </TabsContent>

              <TabsContent
                value="sobre"
                className="flex flex-col gap-4 lg:flex-row lg:items-start"
              >
                <div className="flex-1 rounded-[16px] bg-white p-4">
                  <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Sobre</span>
                  <p
                    className="mb-0 mt-3 text-sm font-semibold leading-relaxed"
                    style={{ color: '#222731' }}
                  >
                    {selectedProject.about}
                  </p>
                </div>

                <div className="flex flex-col gap-4 lg:w-[350px] lg:flex-none">
                  <PlayfulCard className="p-5">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <h4
                        className="mb-0 text-sm font-extrabold"
                        style={{ color: '#222731' }}
                      >
                        Participantes
                      </h4>
                      {!selectedProject.approved && (usePermission('Adicionar participantes', 'Projetos') || usePermission('Excluir participantes', 'Projetos')) && (
                        <Dropdown>
                          <Dropdown.Toggle>
                            <FiMoreHorizontal />
                          </Dropdown.Toggle>
                          <Dropdown.Content>
                            {usePermission('Adicionar participantes', 'Projetos') && (
                              <Dropdown.Item
                                onClick={() => {
                                  getAllStudents(allProfiles);
                                  setShowModal({ ...showModal, addParticipant: true });
                                }}
                              >
                                {t('courses.addParticpants')}
                              </Dropdown.Item>
                            )}
                            {usePermission('Excluir participantes', 'Projetos') && (
                              <Dropdown.Item
                                onClick={() => {
                                  getAllStudents();
                                  setShowModal({ ...showModal, removeParticipant: true });
                                }}
                              >
                                {t('courses.deleteParticipants')}
                              </Dropdown.Item>
                            )}
                          </Dropdown.Content>
                        </Dropdown>
                      )}
                    </div>

                    {selectedProject?.users?.length > 0 ? (
                      <CardAvatarStack
                        data={selectedProject?.users}
                        modalTitle={t('projects.titleProjectParticipants')}
                        disableUserLink
                      />
                    ) : (
                      authorityLevel === 'admin' &&
                      !selectedProject.approved && (
                        <Button
                          variant="link"
                          size="xs"
                          className="font-light text-neutral-dark"
                          onClick={() => {
                            getAllStudents();
                            setShowModal({ ...showModal, addParticipant: true });
                          }}
                        >
                          Vamos adicionar estudantes?
                        </Button>
                      )
                    )}
                  </PlayfulCard>

                  <ProjectSchedulePlayful />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}

      <Dialog
        open={showModal.addParticipant}
        onOpenChange={() => setShowModal({ ...showModal, addParticipant: false })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projects.selectParticipants')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <Input
              className="sticky top-0 z-10"
              type="search"
              placeholder={t('courses.searchParticipants')}
              aria-label={t('courses.searchParticipants')}
              onChange={e => studentFilter(e.target.value)}
            />
            {filteredStudents?.length > 0 &&
              filteredStudents.map(student => (
                <Card.StudentEmail
                  student={student}
                  profileImage={student.image.url}
                  registered
                  key={student.id}
                  studentId={student.id}
                  name={student.name}
                  studentEmail={student.email}
                  buttonText={t('button.add')}
                  onClick={() => {
                    setShowModal({ ...showModal, addParticipant: false });
                    addStudentToSelectedProject(student.id);
                  }}
                />
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showModal.removeParticipant}
        onOpenChange={() => setShowModal({ ...showModal, removeParticipant: false })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('courses.deleteParticipants')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {selectedProject?.users?.length > 0 ? (
              selectedProject?.users.map(u => (
                <Card.StudentEmail
                  student={u}
                  registered
                  key={u.id}
                  name={u.name}
                  studentEmail={u.email}
                  buttonText={t('button.remove')}
                  photo={u.image.url}
                  onClick={() => {
                    destroyStudentForm(u);
                  }}
                />
              ))
            ) : (
              <p className="mb-4">Nenhum participante incluído no projeto</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
